import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { useStaffText } from '../ui/StaffText'
import { areaAt, areaKey, hexToHsv, hsvToHex, hueAt, hueKey, parseHex, type Hsv } from './colourMath'

const START = '#d4192c'
const WIDTH = 232

/**
 * The custom colour swatch and its picker: an area, a hue slider and the hex.
 * Our own because the browser's picker opens in RGB and a page cannot change that.
 */
export function ColourPicker({ value, label, onPick }: { value: string; label: string; onPick: (hex: string) => void }) {
  const { say } = useStaffText()
  const literal = parseHex(value) ?? ''
  const [open, setOpen] = useState(false)
  const [hsv, setHsv] = useState<Hsv>(() => hexToHsv(literal || START))
  const [draft, setDraft] = useState(literal || START)
  const [box, setBox] = useState<{ top: number; left: number } | null>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const pop = useRef<HTMLDivElement>(null)
  const hexField = useRef<HTMLInputElement>(null)
  const id = useId()
  const sent = useRef(literal)

  // A value set from outside (a swatch, undo) moves the picker; our own echo does not.
  useEffect(() => {
    sent.current = literal
    if (!literal || literal === hsvToHex(hsv)) return
    setHsv(hexToHsv(literal))
    setDraft(literal)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [literal])

  const place = useCallback(() => {
    const r = trigger.current?.getBoundingClientRect()
    if (!r) return
    const height = pop.current?.offsetHeight ?? 260
    const top = window.innerHeight - r.bottom < height + 12 && r.top > height + 12 ? r.top - 6 - height : r.bottom + 6
    setBox({ top, left: Math.max(8, Math.min(r.left, window.innerWidth - WIDTH - 8)) })
  }, [])

  useLayoutEffect(() => {
    if (!open) return
    place()
    hexField.current?.focus()
    hexField.current?.select()
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, place])

  const pick = (hex: string) => {
    if (hex === sent.current) return
    sent.current = hex
    onPick(hex)
  }

  const apply = (next: Hsv) => {
    setHsv(next)
    const hex = hsvToHex(next)
    setDraft(hex)
    pick(hex)
  }

  const close = useCallback((focus: boolean) => {
    setOpen(false)
    if (focus) trigger.current?.focus()
  }, [])

  useEffect(() => {
    if (!open) return
    const away = (e: globalThis.PointerEvent) => {
      const t = e.target as Node
      if (!trigger.current?.contains(t) && !pop.current?.contains(t)) close(false)
    }
    document.addEventListener('pointerdown', away, true)
    return () => document.removeEventListener('pointerdown', away, true)
  }, [open, close])

  const typed = (text: string, final: boolean) => {
    setDraft(text)
    const hex = parseHex(text, final)
    if (!hex) return
    setHsv(hexToHsv(hex))
    pick(hex)
  }

  // Pointer captured on press, so a drag past the edge keeps steering.
  const drag = (at: (e: PointerEvent<HTMLDivElement>, r: DOMRect) => Hsv) => ({
    onPointerDown: (e: PointerEvent<HTMLDivElement>) => {
      e.currentTarget.setPointerCapture(e.pointerId)
      apply(at(e, e.currentTarget.getBoundingClientRect()))
    },
    onPointerMove: (e: PointerEvent<HTMLDivElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) apply(at(e, e.currentTarget.getBoundingClientRect()))
    },
  })

  const keys = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Escape' && e.key !== 'Enter') return
    e.preventDefault()
    const hex = parseHex(draft)
    if (e.key === 'Enter' && e.target === hexField.current && hex) {
      setHsv(hexToHsv(hex))
      pick(hex)
    }
    close(true)
  }

  const s = Math.round(hsv.s * 100)
  const v = Math.round(hsv.v * 100)
  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="studio-swatch studio-swatch--pick"
        style={literal ? { background: literal } : undefined}
        title={literal || say('designs.insp.anotherColour')}
        aria-label={`${label}${literal ? ` ${literal}` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (open) return close(false)
          setDraft(hsvToHex(hsv))
          setOpen(true)
        }}
      />
      {open &&
        createPortal(
          <div
            ref={pop}
            role="dialog"
            aria-label={label}
            className="studio-picker"
            style={{ top: box?.top ?? -9999, left: box?.left ?? -9999, width: WIDTH }}
            onKeyDown={keys}
          >
            <div
              className="studio-picker__area"
              style={{ backgroundColor: hsvToHex({ h: hsv.h, s: 1, v: 1 }) }}
              role="slider"
              tabIndex={0}
              aria-label={say('designs.insp.shade')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={s}
              aria-valuetext={`${s}%, ${v}%`}
              onKeyDown={(e) => {
                const next = areaKey(hsv, e.key, e.shiftKey)
                if (!next) return
                e.preventDefault()
                apply(next)
              }}
              {...drag((e, r) => areaAt(hsv.h, e.clientX - r.left, e.clientY - r.top, r.width, r.height))}
            >
              <span className="studio-picker__dot" style={{ left: `${s}%`, top: `${100 - v}%`, background: hsvToHex(hsv) }} />
            </div>
            <div
              className="studio-picker__hue"
              role="slider"
              tabIndex={0}
              aria-label={say('designs.insp.hue')}
              aria-valuemin={0}
              aria-valuemax={360}
              aria-valuenow={Math.round(hsv.h)}
              onKeyDown={(e) => {
                const h = hueKey(hsv.h, e.key, e.shiftKey)
                if (h === null) return
                e.preventDefault()
                apply({ ...hsv, h })
              }}
              {...drag((e, r) => ({ ...hsv, h: hueAt(e.clientX - r.left, r.width) }))}
            >
              <span className="studio-picker__dot" style={{ left: `${(hsv.h / 360) * 100}%`, background: hsvToHex({ h: hsv.h, s: 1, v: 1 }) }} />
            </div>
            <label className="studio-picker__hex" htmlFor={id}>
              <span>{say('designs.insp.hex')}</span>
              <input
                ref={hexField}
                id={id}
                spellCheck={false}
                autoComplete="off"
                maxLength={7}
                value={draft}
                onChange={(e) => typed(e.target.value, (e.nativeEvent as InputEvent).inputType === 'insertFromPaste')}
                onBlur={() => {
                  // A short #abc still typed applies on leaving; a broken one goes back.
                  const hex = parseHex(draft)
                  if (!hex) setDraft(hsvToHex(hsv))
                  else if (hex !== hsvToHex(hsv)) typed(draft, true)
                }}
              />
            </label>
          </div>,
          document.body,
        )}
    </>
  )
}
